import React from 'react';
import { motion } from 'framer-motion';
import PhotoPreviewCard from './PhotoPreviewCard';
import Icon from '../../../components/AppIcon';

const PreviewGallery = ({ files, onRemove, onClearAll, maxFiles, onAddMore, creditBalance = 0 }) => {
  if (files?.length === 0) return null;

  const usedSlots = files?.length;
  const hasPremiumAccess = creditBalance > 0;

  // Free users: can upload up to 3 photos, then 6 locked slots shown
  // Premium users (have credits): can upload up to maxFiles (9), no locked slots
  const uploadLimit = hasPremiumAccess ? maxFiles : 3;
  const showAddPhotoButton = usedSlots < uploadLimit;
  const lockedSlotsCount = hasPremiumAccess ? 0 : Math.max(0, maxFiles - Math.max(usedSlots, 3));

  return (
    <div className="space-y-4">
      {/* Header with Title and Slot Counter */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">
          Your Lineup
        </h2>
        <div className="px-3 py-1.5 rounded-full bg-purple-100 border border-purple-200">
          <span className="text-sm font-semibold">
            <span className="text-purple-600">{files?.length}</span>
            <span className="text-gray-500">/{maxFiles}</span>
            <span className="text-gray-600 ml-1">Slots</span>
          </span>
        </div>
      </div>
      {/* Grid with Photos + Add Photo Placeholder */}
      <div className="grid grid-cols-3 gap-3">
        {files?.map((file, index) => (
          <PhotoPreviewCard
            key={file?.id}
            file={file}
            index={index}
            onRemove={() => onRemove(file?.id)}
          />
        ))}
        
        {/* Add Photo Placeholder - Show if under upload limit */}
        {showAddPhotoButton && (
          <motion.label
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileTap={{ scale: 0.95 }}
            className="aspect-square rounded-2xl border-2 border-dashed border-purple-300 bg-purple-50 flex flex-col items-center justify-center cursor-pointer hover:bg-purple-100 hover:border-purple-400 transition-all"
          >
            <input
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/heic,image/jpg"
              multiple
              onChange={(e) => {
                const newFiles = Array.from(e?.target?.files || []);
                if (newFiles?.length > 0) {
                  onAddMore(newFiles);
                }
                e.target.value = '';
              }}
            />
            <div className="w-12 h-12 rounded-full bg-white border-2 border-purple-400 flex items-center justify-center mb-2">
              <Icon name="Plus" size={24} className="text-purple-600" />
            </div>
            <span className="text-sm font-semibold text-purple-600">Add Photo</span>
          </motion.label>
        )}
        
        {/* Locked Slots - Only shown for users without credits */}
        {Array.from({ length: lockedSlotsCount })?.map((_, index) => (
          <div
            key={`locked-${index}`}
            className="aspect-square rounded-2xl bg-gray-100 border border-gray-200 flex flex-col items-center justify-center"
          >
            <Icon name="Lock" size={24} className="text-gray-400 mb-1" />
            <span className="text-xs font-medium text-gray-500">Locked</span>
          </div>
        ))}
      </div>
      {/* Signpost below locked placeholders - only for free users */}
      {!hasPremiumAccess && (
        <div className="text-center pt-2">
          <p className="text-sm font-medium text-gray-600">
            Purchase credits to compare more photos
          </p>
        </div>
      )}
    </div>
  );
};

export default PreviewGallery;